import 'react-native-gesture-handler';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { AuthProvider, AuthContext } from './src/context/AuthContext';
import LoginScreen from './src/screens/LoginScreen';
import RegisterScreen from './src/screens/RegisterScreen';
import HomeScreen from './src/screens/HomeScreen';
import AddScoreScreen from './src/screens/AddScoreScreen';
import StatsScreen from './src/screens/StatsScreen';
import MessagesScreen from './src/screens/MessagesScreen';
import SettingsScreen from './src/screens/SettingsScreen';
import Colors from './src/constants/Colors';
import { showAlert } from './src/services/confirm';
import api from './src/services/api';

const Stack = createStackNavigator();
const Tab = createBottomTabNavigator();
const theme = Colors.light;

function MainTabBar({ state, descriptors, navigation }) {
    const insets = useSafeAreaInsets();
    const { user } = useContext(AuthContext);
    const [unreadCount, setUnreadCount] = useState(0);

    const loadUnreadCount = useCallback(async () => {
        if (!user?.partnerId) {
            setUnreadCount(0);
            return;
        }

        try {
            const response = await api.get('/chat/unread-count');
            const count = Number(response?.data?.count || 0);
            setUnreadCount(Number.isFinite(count) ? count : 0);
        } catch (error) {
            // Keep current badge state when polling fails.
        }
    }, [user?.partnerId]);

    useEffect(() => {
        loadUnreadCount();
        const timer = setInterval(loadUnreadCount, 3000);
        return () => clearInterval(timer);
    }, [loadUnreadCount]);

    const leftRoutes = ['Home', 'Stats']
        .map((name) => state.routes.find((route) => route.name === name))
        .filter(Boolean);
    const rightRoutes = ['Messages', 'Settings']
        .map((name) => state.routes.find((route) => route.name === name))
        .filter(Boolean);

    const renderTabButton = (route) => {
        const { options } = descriptors[route.key];
        const index = state.routes.findIndex((r) => r.key === route.key);
        const isFocused = state.index === index;

        const onPress = () => {
            const event = navigation.emit({
                type: 'tabPress',
                target: route.key,
                canPreventDefault: true,
            });

            if (!isFocused && !event.defaultPrevented) {
                navigation.navigate(route.name);
            }
        };

        const onLongPress = () => {
            navigation.emit({
                type: 'tabLongPress',
                target: route.key,
            });
        };

        const color = isFocused ? theme.tint : theme.tabIconDefault;
        const icon = options.tabBarIcon?.({ focused: isFocused, color, size: 22 });
        const label = options.tabBarLabel ?? options.title ?? route.name;
        const showUnreadBadge = route.name === 'Messages' && unreadCount > 0;
        const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

        return (
            <TouchableOpacity
                key={route.key}
                accessibilityRole="button"
                accessibilityState={isFocused ? { selected: true } : {}}
                accessibilityLabel={options.tabBarAccessibilityLabel}
                testID={options.tabBarTestID}
                onPress={onPress}
                onLongPress={onLongPress}
                style={styles.tabBtn}
                activeOpacity={0.8}
            >
                <View style={styles.iconWrap}>
                    {icon}
                    {showUnreadBadge && (
                        <View style={styles.unreadBadge}>
                            <Text style={styles.unreadBadgeText}>{badgeLabel}</Text>
                        </View>
                    )}
                </View>
                <Text style={[styles.tabLabel, { color }]}>{label}</Text>
            </TouchableOpacity>
        );
    };

    const addDisabled = !user?.partnerId;

    const handleAddPress = async () => {
        if (addDisabled) {
            await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning);
            showAlert('提示', '请先完成配对后再记一笔评分。');
            return;
        }

        await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
        navigation.getParent()?.navigate('AddScore');
    };

    return (
        <View
            style={[
                styles.tabBar,
                {
                    paddingBottom: Math.max(insets.bottom, 10),
                },
            ]}
        >
            <View style={styles.tabSide}>{leftRoutes.map(renderTabButton)}</View>

            <View style={styles.tabCenter}>
                <TouchableOpacity
                    onPress={handleAddPress}
                    activeOpacity={0.9}
                    style={styles.addBtnHit}
                    accessibilityRole="button"
                    accessibilityLabel="记一笔"
                >
                    <View style={styles.addBtnWrap}>
                        <View style={[styles.addBtnLayer, styles.addBtnLayerLeft]} />
                        <View style={[styles.addBtnLayer, styles.addBtnLayerRight]} />
                        <View style={[styles.addBtnMain, addDisabled && styles.addBtnMainDisabled]}>
                            <Ionicons
                                name="add"
                                size={26}
                                color={addDisabled ? theme.textTertiary : theme.text}
                            />
                        </View>
                    </View>
                </TouchableOpacity>
            </View>

            <View style={styles.tabSide}>{rightRoutes.map(renderTabButton)}</View>
        </View>
    );
}

function HomeTabs() {
    return (
        <Tab.Navigator
            screenOptions={({ route }) => ({
                tabBarIcon: ({ focused, color, size }) => {
                    let iconName;

                    if (route.name === 'Home') {
                        iconName = focused ? 'home' : 'home-outline';
                    } else if (route.name === 'Stats') {
                        iconName = focused ? 'bar-chart' : 'bar-chart-outline';
                    } else if (route.name === 'Messages') {
                        iconName = focused ? 'chatbubble-ellipses' : 'chatbubble-ellipses-outline';
                    } else if (route.name === 'Settings') {
                        iconName = focused ? 'settings' : 'settings-outline';
                    }

                    return <Ionicons name={iconName} size={size} color={color} />;
                },
                headerShown: false,
            })}
            tabBar={(props) => <MainTabBar {...props} />}
        >
            <Tab.Screen
                name="Home"
                component={HomeScreen}
                options={{ title: '首页' }}
            />
            <Tab.Screen
                name="Stats"
                component={StatsScreen}
                options={{ title: '统计' }}
            />
            <Tab.Screen
                name="Settings"
                component={SettingsScreen}
                options={{ title: '设置' }}
            />
            <Tab.Screen
                name="Messages"
                component={MessagesScreen}
                options={{ title: '消息' }}
            />
        </Tab.Navigator>
    );
}

function AppNavigator() {
    const { user, loading } = useContext(AuthContext);

    if (loading) {
        return null;
    }

    return (
        <NavigationContainer
            key={user ? 'auth-user' : 'auth-guest'}
            documentTitle={{
                formatter: (options) =>
                    options?.title ? `${options.title} - 情侣评分系统` : '情侣评分系统',
            }}
        >
            <Stack.Navigator
                key={user ? 'user-stack' : 'guest-stack'}
                screenOptions={{ cardStyle: { backgroundColor: theme.background } }}
            >
                {user ? (
                    <>
                        <Stack.Screen
                            name="Main"
                            component={HomeTabs}
                            options={{ headerShown: false }}
                        />
                        <Stack.Screen
                            name="AddScore"
                            component={AddScoreScreen}
                            options={{
                                presentation: 'modal',
                                headerShown: false,
                            }}
                        />
                    </>
                ) : (
                    <>
                        <Stack.Screen name="Login" component={LoginScreen} options={{ headerShown: false }} />
                        <Stack.Screen name="Register" component={RegisterScreen} options={{ headerBackTitle: '返回' }} />
                    </>
                )}
            </Stack.Navigator>
            <StatusBar style="dark" translucent={false} />
        </NavigationContainer>
    );
}

export default function App() {
    return (
        <GestureHandlerRootView style={{ flex: 1 }}>
            <AuthProvider>
                <AppNavigator />
            </AuthProvider>
        </GestureHandlerRootView>
    );
}

const styles = StyleSheet.create({
    tabBar: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        backgroundColor: theme.backgroundSecondary,
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: theme.border,
        paddingTop: 8,
    },
    tabSide: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'space-around',
        alignItems: 'flex-end',
    },
    tabCenter: {
        width: 96,
        alignItems: 'center',
        justifyContent: 'flex-end',
        paddingBottom: 4,
    },
    tabBtn: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 6,
    },
    iconWrap: {
        position: 'relative',
    },
    tabLabel: {
        marginTop: 2,
        fontSize: 10,
        fontWeight: '500',
    },
    unreadBadge: {
        position: 'absolute',
        top: -7,
        right: -12,
        minWidth: 16,
        height: 16,
        borderRadius: 8,
        paddingHorizontal: 4,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#FF3B30',
    },
    unreadBadgeText: {
        color: '#fff',
        fontSize: 9,
        fontWeight: '700',
    },
    addBtnHit: {
        marginTop: -18,
    },
    addBtnWrap: {
        width: 58,
        height: 38,
        alignItems: 'center',
        justifyContent: 'center',
    },
    addBtnLayer: {
        position: 'absolute',
        width: 58,
        height: 38,
        borderRadius: 12,
    },
    addBtnLayerLeft: {
        backgroundColor: theme.tint,
        left: -6,
    },
    addBtnLayerRight: {
        backgroundColor: theme.primary,
        left: 6,
    },
    addBtnMain: {
        width: 58,
        height: 38,
        borderRadius: 12,
        backgroundColor: theme.backgroundSecondary,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: theme.border,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.12,
        shadowRadius: 10,
        elevation: 6,
    },
    addBtnMainDisabled: {
        opacity: 0.6,
    },
});
